import React, {useState} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import Icon from '@/components/icons/Icon';
import Spinner from '@/components/ui/Spinner';

import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import {navigate} from '@/reach-router-compat';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {buildTeamAnalyticsURL} from '@/utils/urlBuilder';
import usePaginatedData from '@/utils/usePaginatedData';
import TeamSettingsModal from './TeamSettingsModal';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import useData from '@/utils/useData';
import {ROUTES} from '@/routing/route';
import TableComponent from '@/components/table/Table';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface Team {
    id: number;
    name: string;
    shortname: string;
    updatedOn?: string;
    createdOn: string;
    nrOfMembers: number;
}

interface PersonStory {
    id: number;
    assignedUrl: string;
    firstStoryboardHashid: string;
    firstStoryboardSlug: string;
}

interface UserData {
    permissions?: string[];
}

interface Column {
    id?: string;
    header: string;
    accessorFn?: (cell: any) => string;
    accessorKey?: string;
    cell?: (cell: any) => React.ReactNode;
}

const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const TeamListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedTeamId, setSelectedTeamId] = useState<number>(0);

    const [searchQuery, setSearchQuery] = useState<string>('');

    const canCreateTeam = hasPermission('fs.team.create', userData?.permissions, true);
    const canChangeTeam = hasPermission('fs.team.update', userData?.permissions, true);
    const userCanAccessSpecialStoryViewObject = hasPermission('fs.story.list', userData?.permissions, true);
    const userCanAccessTeamsResource = hasPermission('fs.team.read', userData?.permissions, true);

    const [personsStories] = useData(window.URLS.storiesResource, {
        storeResolver: 'personsStories',
        initialValue: [],
        requestParams: {
            params: {storyListType: 'personsStories'},
        },
    });

    const {
        data: teams,
        loading: loadingTeams,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.teamsResource, {
        storeResolver: 'teams',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });



    if (loadingUserData) {
        return (
            <Spinner
                size="md"
                text={i18n.t('jt_loading_data_general.text')}
                centered
                fullHeight
            />
        );
    }

    if (!userCanAccessTeamsResource) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_here_you_see_list_of_teams.text')} />
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (teamId: number): void => {
        setSelectedTeamId(teamId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedTeamId(0);
    };


    const buildStoryboardUrl = (story: PersonStory): { url: string; storyType: string } => {
        if (!story?.assignedUrl || story.assignedUrl !== 'teams') {
            return {url: '#', storyType: 'template'};
        }

        if (story.id > 0) {
            if (!story.firstStoryboardHashid || !story.firstStoryboardSlug) {
                console.warn('Missing storyboard data for special story:', story);
                return {url: '#', storyType: 'template'};
            }

            const url = ROUTES.storyboardViewRoute
                .replace(':activeStoryboardHashid', story.firstStoryboardHashid)
                .replace(':activeStoryboardUrl', story.firstStoryboardSlug);
            return {url, storyType: 'special'};
        } else {
            if (!story.assignedUrl || !story.firstStoryboardSlug) {
                console.warn('Missing template story data:', story);
                return {url: '#', storyType: 'template'};
            }

            const url = ROUTES.storyboardTemplateViewRoute
                .replace(':activeStoryUrl', story.assignedUrl)
                .replace(':activeStoryboardUrl', story.firstStoryboardSlug);
            return {url, storyType: 'template'};
        }
    };

    let url: string = '#';
    let storyType: string = 'template';

    const teamsStory = (personsStories as PersonStory[]).find((story: PersonStory) =>
        story?.assignedUrl === 'teams'
    );

    if (teamsStory) {
        const result = buildStoryboardUrl(teamsStory);
        url = result.url;
        storyType = result.storyType;
    }

    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const teamPiiRestricted = restrictedFieldKeys.includes('team_pii');

    const detailUrlGenerator = (rowData: Team): string => {
        return buildTeamAnalyticsURL(rowData.id);
    };

    const columns: Column[] = [
        {
            id: 'name',
            header: i18n.t('th_name.text'),
            accessorFn: (cell: Team) => {
                if (!cell.name || cell.name === '-') {
                    return '-';
                }
                return cell.name;
            },
            cell: (cell: any) => {
                if (teamPiiRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />;
            },
        },
        {
            id: 'shortname',
            header: i18n.t('th_unique.text'),
            accessorFn: (cell: Team) => {
                if (!cell.shortname || cell.shortname === '-') {
                    return '-';
                }
                return cell.shortname;
            },
            cell: (cell: any) => {
                if (teamPiiRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return cell.getValue() || '-';
            },
        },
        {
            id: 'lastModified',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: Team) => {
                if (!cell.updatedOn || cell.updatedOn === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.updatedOn, true, {setZone: false},
                ) : '';
            },
        },
        {
            header: i18n.t('th_members.text'),
            accessorKey: 'nrOfMembers',
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <div className="d-flex align-items-center justify-content-end w-100">
                    <div className="d-inline-block mr-2">
                        {
                            userCanAccessSpecialStoryViewObject ? (
                                <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        icon="mdi-eye"
                                        onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                        data-cy="team-view-button"
                                    >
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                    placement="top"
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        cursor: 'not-allowed',
                                    }}
                                    >
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            icon="mdi-eye"
                                            disabled
                                            style={{pointerEvents: 'none'}}
                                            data-cy="team-list-eye-outlinesecondary-btn">
                                        </Button>
                                    </div>
                                </Tooltip>
                            )
                        }
                    </div>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                data-cy="team-settings-toggle"
                                id="settings-dropdown-1"
                            />

                            <Dropdown.Menu align="end">
                                {
                                    canChangeTeam ? (
                                        <Dropdown.Item
                                            onClick={() => openSettingsModal(cell.row.original.id)}
                                            data-cy="team-settings-button"
                                        >
                                            <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item
                                                    style={{pointerEvents: 'none'}}
                                                    disabled
                                                    data-cy="team-list-btn">
                                                    <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_here_you_see_list_of_teams.text')} />

            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="team-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        canCreateTeam ? (
                            <Button
                                variant="info"
                                size="sm"
                                className='mb-2'
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-team"
                            >
                                {i18n.t('bn_add_new_team.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'inline-block'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        className='mb-2'
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="team-list-plus-btn"
                                    >
                                        {i18n.t('bn_add_new_team.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            <LoadingWrapper
                loading={loadingTeams}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body">
                            <TableComponent data={teams} columns={columns} />

                            <PaginationComponent
                                pages={pages}
                                currentPage={currentPage}
                                onPageChange={setCurrentPage}
                                hideWhenSinglePage={false}
                            />
                    </div>
                </div>

                {
                    settingsModalOpen ? (
                        <TeamSettingsModal
                            modalOpen={settingsModalOpen}
                            closeModal={closeSettingsModal}
                            activeTeamId={selectedTeamId}
                        /> as any
                    ) : null
                }
            </LoadingWrapper>

        </>
    );
});

export default TeamListView; 